import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getSigner, getWeb3Provider } from '@dynamic-labs/ethers-v6'
import * as Sentry from '@sentry/browser'
import {
  setAccount,
  setBalanceEth,
  setBalanceTokenSale,
  setEthUsdPrice,
} from '../../modules/web3/actions'
import {
  selectAccount,
  selectActiveTokenSaleAddress,
  selectDesiredNetworkId,
} from '../../modules/web3/selectors'
import useStakingContract, { IStakingContract } from '../useStakingContract'
import useStakingV1Contract, {
  IStakingV1Contract,
} from '../useStakingV1Contract'
import useMasterContract, { IMasterContract } from '../useMasterContract'
import useMasterRpcContract, {
  IMasterRpcContract,
} from '../useMasterRpcContract'
import useStakingContractOld, { IStakingContractOld } from '../useStakingOld'
import useLPTokenContract, { ILPTokenContract } from '../useLPTokenContract'
import useAccessContract, { IAccessContract } from '../useAccessContract'
import useRouterContract, { IRouterContract } from '../useRouterContract'
import useAdminContract, { IAdminContract } from '../useAdminContract'
import usePoolsData, { IPoolsWorkingData } from '../pools/usePoolsData'
import useBUSDContract, { IBUSDContract } from '../useBUSDContract'
import { verifyUserWithDynamicJwt } from './utils'
import { toast } from 'react-toastify'
import { theseus } from '../../api/theseus'
import { setLithiumAccount } from '../../modules/user/slice'
import { selectUser } from '../../modules/user/selectors'
import {
  setDiscordWarningNotice,
  setTwitterWarningNotice,
} from '../../modules/miscUi/actions'
import { useLazyGetEthUsdPriceQuery } from '../../modules/web3/api'
import {
  useDynamicContext,
  useIsLoggedIn as useIsLoggedInDynamic,
  useRefreshUser,
  useRpcProviders,
  useSwitchWallet,
  useUserWallets,
  useWalletConnectorEvent,
} from '@dynamic-labs/sdk-react-core'
import { ethers } from 'ethers'
import { BASE, BLAST } from '../../constants'
import { isWalletConnectConnector } from '@dynamic-labs/wallet-connector-core'
import {
  evmProvidersSelector,
  isEthereumWallet,
} from '@dynamic-labs/ethereum-core'
import { isZeroDevConnector } from '@dynamic-labs/ethereum-aa'
import { setupCoinbaseButtonOverride } from './coinbaseOnramp'
import { NETWORK_VERSION } from './constants'
import { SiweMessage } from 'siwe'

import { getAuthToken } from '@dynamic-labs/sdk-react-core'

const FIVE_MINUTES_IN_MILLISECONDS = 300000

interface IWeb3Context {
  bscNetwork: undefined | ethers.JsonRpcProvider
  ethereumNetwork: undefined | ethers.JsonRpcProvider
  polygonNetwork: undefined | ethers.JsonRpcProvider
  blastProvider: undefined | ethers.JsonRpcProvider
  baseNetwork: undefined | ethers.JsonRpcProvider
  poolsData: IPoolsWorkingData
  initWeb3: () => void
  updateBalance: (connectedAccount: string) => Promise<void>
  logout: () => Promise<void>
  isMetaMaskMobile: boolean
  isAuthenticating: boolean
  getReadOnlyContract: (address: string, abi: any) => null | ethers.Contract
  getWriteOnlyContract: (address: string, abi: any) => null | ethers.Contract
  provider: null | ethers.BrowserProvider
  signer: null | ethers.Signer
  openOnramp: () => void
  contracts: {
    master: IMasterContract
    masterRpc: IMasterRpcContract
    lpToken: ILPTokenContract
    staking: IStakingContract
    stakingV1: IStakingV1Contract
    admin: IAdminContract
    access: IAccessContract
    router: IRouterContract
    stakingOld: IStakingContractOld
    busd: IBUSDContract
  }
}

export type SocialDisconnectEvent = {
  id: string
  type: string
  owner_id: string
  project_id: number
  attachment_id: null
  meta_data: {
    type: 'discord' | 'twitter'
    handle: string
    time_of_error: string
  }
  createdAt: string
  updatedAt: string
}

export async function methodPromiseWithRetry(
  promise: Promise<any>,
  retries = 3,
  cooldown = 3000,
) {
  for (let i = 0; i < retries; i++) {
    try {
      return await promise
    } catch (error: any) {
      const is429 = error.message.includes('429')
      if (is429) {
        console.warn(
          `Request rate limit hit, retrying after cooldown (${
            i + 1
          }/${retries})`,
        )
        toast.error('Request rate limit hit, retrying in 3 second...')
        await new Promise((resolve) => setTimeout(resolve, cooldown))
      } else {
        throw error
      }
    }
  }

  throw new Error('Exceeded maximum retries')
}

const Web3Context = createContext({} as IWeb3Context)

export const Web3Provider: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch()

  const isConnected = useIsLoggedInDynamic()
  const {
    setShowAuthFlow,
    handleLogOut,
    primaryWallet,
    user: dynamicUser,
    network: chainId,
    sdkHasLoaded,
    multiWalletWidgetState,
    loadingNetwork,
  } = useDynamicContext()
  const { address } = primaryWallet ?? {}

  const dynamicJwtToken = getAuthToken()

  console.log({ dynamicJwtToken })

  const isMetaMaskMobile =
    primaryWallet?.key === 'metamask' &&
    primaryWallet?.connector?.isWalletConnect

  const desiredNetworkId = useSelector(selectDesiredNetworkId)
  const wrongNetwork = Number(chainId) !== desiredNetworkId

  const [provider, setProvider] = useState<null | ethers.BrowserProvider>(null)
  const [signer, setSigner] = useState<null | ethers.Signer>(null)
  const evmProviders = useRpcProviders(evmProvidersSelector)

  useEffect(() => {
    if (!primaryWallet) return

    const MAX_RETRY_TIME = 10000
    const RETRY_INTERVAL = 200

    const retryWithTimeout = async (fn, setter) => {
      const startTime = Date.now()
      let didSucceed = false

      const tryFetch = async () => {
        if (didSucceed) return
        try {
          const result = await fn()
          setter(result)
          didSucceed = true
        } catch (error) {
          if (Date.now() - startTime < MAX_RETRY_TIME) {
            setTimeout(tryFetch, RETRY_INTERVAL)
            console.warn('Failed to get provider/signer - retrying')
          } else {
            console.error(
              `Failed to get provider/signer after ${
                MAX_RETRY_TIME / 1000
              } seconds`,
              error,
            )
          }
        }
      }

      tryFetch()
    }

    retryWithTimeout(() => getWeb3Provider(primaryWallet), setProvider)
    retryWithTimeout(() => getSigner(primaryWallet), setSigner)
  }, [primaryWallet])

  const switchWallet = useSwitchWallet()
  const userWallets = useUserWallets()
  const [
    hasSwitchedToBrandedWalletOnFirstConnect,
    setHasSwitchedToBrandedWalletOnFirstConnect,
  ] = useState(false)
  useEffect(() => {
    if (!primaryWallet) return

    async function logWalletInfo() {
      const { connector } = primaryWallet ?? {}
      if (!connector) return

      const supportedNetworks = isWalletConnectConnector(connector)
        ? await connector.getSupportedNetworks()
        : undefined

      console.info('Wallet Connector', {
        walletName: connector.name,
        supportedNetworks: supportedNetworks,
        supportsNetworkSwitching: connector.supportsNetworkSwitching(),
        supportedChains: connector.supportedChains,
      })

      if (
        isZeroDevConnector(connector) &&
        !hasSwitchedToBrandedWalletOnFirstConnect
      ) {
        const brandedWallets = userWallets.filter(
          (w) => w.address !== primaryWallet.address && w.key !== 'turnkeyhd',
        )
        if (brandedWallets.length > 0) {
          console.info('Branded wallets', brandedWallets)
          console.info('Switching to other wallet', brandedWallets[0])
          switchWallet(brandedWallets[0].id)
          setHasSwitchedToBrandedWalletOnFirstConnect(true)
        }
      }
    }

    logWalletInfo()

    console.info('EVM Providers', evmProviders)
  }, [
    evmProviders,
    hasSwitchedToBrandedWalletOnFirstConnect,
    primaryWallet,
    switchWallet,
    userWallets,
  ])

  useWalletConnectorEvent(
    primaryWallet?.connector,
    'accountChange',
    async ({ accounts, ...rest }, connector) => {
      console.group('Account Changed')
      console.info('Accounts', accounts, rest)
      console.info('Connector that emitted', connector)
      console.info('Logging out')
      await logout()
      console.groupEnd()
    },
  )

  const wallets = useUserWallets()
  const walletsConnectors = wallets.map(({ connector }) => connector)

  useWalletConnectorEvent(walletsConnectors, 'disconnect', (connector) => {
    console.info(`Connector ${connector} disconnected`)
  })

  //useEffect(() => {
  //  getNetwork(primaryWallet?.connector).then(async (network) => {
  //    const supportsNetworkSwitching =
  //      primaryWallet?.connector?.supportsNetworkSwitching()
  //    console.info(`Connected to network ${network}`, {
  //      supportsNetworkSwitching,
  //      baseChainId: BASE.chainId,
  //    })
  //    if (
  //      supportsNetworkSwitching &&
  //      String(network) !== String(BASE.chainId)
  //    ) {
  //      try {
  //        console.info(`Switching to network ${BASE.chainId}`)
  //        return await primaryWallet?.connector?.switchNetwork({
  //          networkChainId: BASE.chainId,
  //        })
  //      } catch (error) {
  //        console.error('Error switching network', error)
  //      }
  //    }
  //  })
  //}, [primaryWallet])

  const onrampInstance = useRef<any>(null)

  const openOnramp = useCallback(() => {
    if (!onrampInstance.current) {
      console.warn('No onramp instance')
      return
    }
    onrampInstance.current.open()
  }, [onrampInstance])

  useEffect(() => {
    let cleanup

    const initOnramp = async () => {
      if (!isEthereumWallet(primaryWallet)) {
        console.log('not an Eth Wallet')
        return
      }

      if (cleanup) {
        cleanup()
      }

      const networks = isWalletConnectConnector(primaryWallet.connector)
        ? await primaryWallet.connector.getSupportedNetworks()
        : undefined

      const res = setupCoinbaseButtonOverride({
        appId: process.env.REACT_APP_COINBASE_APP_ID,
        addresses: {
          [primaryWallet.address]: networks,
        },
        assets: ['ETH'],
        debug: true,
      })
      cleanup = res.cleanup
      onrampInstance.current = res.onrampInstance
    }

    if (isConnected && primaryWallet) {
      initOnramp()
    }

    return () => {
      if (cleanup) {
        cleanup()
        onrampInstance.current = null
      }
    }
  }, [isConnected, primaryWallet])

  const account = useSelector(selectAccount)

  const bscNetwork = undefined
  const ethereumNetwork = undefined
  const polygonNetwork = undefined
  const blastProvider = new ethers.JsonRpcProvider(BLAST.rpcUrl)
  const baseNetwork = new ethers.JsonRpcProvider(BASE.rpcUrl)

  const stakingContract = useStakingContract(provider, account)
  const stakingV1Contract = useStakingV1Contract(provider, account)
  const lpTokenContract = useLPTokenContract(provider, account)
  const masterContract = useMasterContract(provider, account)
  const accessContract = useAccessContract(provider, account)
  const stakingOldContract = useStakingContractOld(provider, account)
  const busdContract = useBUSDContract()

  const masterRpcContract = useMasterRpcContract(blastProvider)
  const adminContract = useAdminContract(baseNetwork)
  const routerContract = useRouterContract(blastProvider)
  const poolsData = usePoolsData(masterRpcContract, adminContract)

  const activeTokenSaleAddress = useSelector(selectActiveTokenSaleAddress)

  const [isAuthenticating, setIsAuthenticating] = useState(false)

  const user = useSelector(selectUser)

  const getReadOnlyContract = useCallback(
    (address: string, abi: any) => {
      if (!provider) {
        console.error('No provider for read only contract', address)
        return null
      }
      return new ethers.Contract(address, abi, provider)
    },
    [provider],
  )

  const getWriteOnlyContract = useCallback(
    (address: string, abi: any) => {
      if (!signer) {
        console.error('No signer for write only contract', address)
        return null
      }
      return new ethers.Contract(address, abi, signer)
    },
    [signer],
  )

  const initWeb3 = useCallback(async () => {
    setShowAuthFlow(true)
  }, [setShowAuthFlow])

  const updateBalance = useCallback(
    async (connectedAccount: string) => {
      try {
        if (!connectedAccount || wrongNetwork || !provider) return
        const ethBalance = await provider.getBalance(connectedAccount)
        dispatch(setBalanceEth(String(ethBalance)))
      } catch (err) {
        console.error(err)
      }
    },
    [wrongNetwork, provider, dispatch],
  )

  const fetchAndSetActiveTokenSaleBalance = useCallback(
    async (connectedAccount: string) => {
      try {
        if (!connectedAccount || !activeTokenSaleAddress) return
        const ERC20_ABI = [
          {
            constant: true,
            inputs: [
              {
                name: '_owner',
                type: 'address',
              },
            ],
            name: 'balanceOf',
            outputs: [
              {
                name: 'balance',
                type: 'uint256',
              },
            ],
            payable: false,
            stateMutability: 'view',
            type: 'function',
          },
        ]
        const contractInstance = getReadOnlyContract(
          activeTokenSaleAddress,
          ERC20_ABI as ethers.InterfaceAbi,
        )
        if (!contractInstance) return
        console.info('Fetch and set active token sale balance', {
          contractInstance,
          connectedAccount,
        })
        const balance = await contractInstance.balanceOf(connectedAccount)

        dispatch(setBalanceTokenSale(String(balance)))
      } catch (err) {
        console.error(err)
      }
    },
    [activeTokenSaleAddress, getReadOnlyContract, dispatch],
  )

  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const logout = useCallback(async () => {
    try {
      setIsLoggingOut(true)
      if (handleLogOut) await handleLogOut()
      await theseus.post('/auth/b2c/logout')
      dispatch(setAccount(null))
      dispatch(setLithiumAccount(null))
      localStorage.removeItem('verified-wallet')
      Sentry.setUser(null)
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoggingOut(false)
    }
  }, [handleLogOut, dispatch])

  const authenticate = useCallback(async () => {
    try {
      console.log('authenticating', {
        isAuthenticating,
        primaryWallet,
        dynamicUser,
        dynamicJwtToken,
      })
      if (!dynamicJwtToken) return
      if (isAuthenticating) return

      setIsAuthenticating(true)

      const referralCode = localStorage.getItem('referralCode')

      const { user: authUser } = await verifyUserWithDynamicJwt(
        dynamicJwtToken,
        {
          referralCode,
        },
      )

      if (!authUser?.walletAddress) throw new Error('No user')
      const { walletAddress } = authUser
      console.log({ authUser }, 'has verified jwt')

      localStorage.setItem('verified-wallet', walletAddress.toLowerCase())
      dispatch(setLithiumAccount(authUser))
      dispatch(setAccount(walletAddress))

      Sentry.setUser({
        id: authUser.id,
        walletAddress: walletAddress.toLowerCase(),
      })
    } catch (err: any) {
      console.error('authenticate error', err)
      const errorMessage = err?.response?.data?.message ?? err.message

      await logout()

      if (errorMessage?.includes('user rejected signing')) {
        toast.error('Please sign in to continue')
      } else if (errorMessage?.includes('rejected')) {
        toast.error('Please accept the sign request in your wallet to continue')
      } else {
        toast.error(
          'There was an error authenticating you. Please ensure you are not using a VPN for this action.',
        )
      }
    } finally {
      setIsAuthenticating(false)
    }
  }, [
    isAuthenticating,
    primaryWallet,
    dynamicUser,
    dispatch,
    logout,
    dynamicJwtToken,
  ])

  const verify = useCallback(
    async ({ address }: { address: string | undefined }) => {
      try {
        console.log('verifying', { address })
        if (!address) throw new Error('No account')

        const res = await theseus.get('/auth/b2c/verify')
        if (res.status !== 200) throw new Error('No user')
        const {
          data: { user, userSocialDisconnectEvents },
        } = res
        const { walletAddress, multiWallets } = user ?? {}
        if (!walletAddress) throw new Error('No user')
        if (
          walletAddress.toLowerCase() !== address?.toLowerCase() &&
          !multiWallets.includes(address?.toLowerCase())
        )
          throw new Error('Wallet address does not match')
        dispatch(setAccount(walletAddress))
        dispatch(setLithiumAccount(user))
        Sentry.setUser({
          id: user.id,
          walletAddress: walletAddress.toLowerCase(),
        })
        if (!!userSocialDisconnectEvents?.length) {
          const sortedSocialDisconnectEvents = userSocialDisconnectEvents.sort(
            (a: SocialDisconnectEvent, b: SocialDisconnectEvent) => {
              return (
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
              )
            },
          )
          for (const {
            meta_data: { type, handle },
          } of sortedSocialDisconnectEvents) {
            if (type === 'twitter') {
              if (!!user?.twitterHandle) continue
              dispatch(
                setTwitterWarningNotice(
                  `You were logged out of your X account (${handle}) due to inactivity, please connect again to resume functionality`,
                ),
              )
            }
            if (type === 'discord') {
              dispatch(
                setDiscordWarningNotice(
                  `You were logged out of your Discord account (${handle}) due to inactivity, please connect again to resume functionality`,
                ),
              )
            }
          }
        }
        localStorage.setItem('verified-wallet', walletAddress.toLowerCase())
      } catch (err) {
        console.error(err)

        console.log({ message: err.message, address, isAuthenticating })
        if (err.message === 'invalid login') {
          if (address && !isAuthenticating && !!dynamicJwtToken) authenticate()
          return
        }
        if (err.message === 'No user') {
          if (address && !isAuthenticating && !!dynamicJwtToken) authenticate()
          return
        }
        if (err.message === 'Wallet address does not match') {
          if (address && !isAuthenticating && !!dynamicJwtToken) authenticate()
          return
        }
      }
    },
    [authenticate, dispatch, isAuthenticating, dynamicJwtToken],
  )

  useEffect(() => {
    if (!!user?.id || !dynamicJwtToken || isAuthenticating) return
    authenticate()
  }, [user, authenticate, dynamicJwtToken, isAuthenticating])

  const [getEthUsdPrice] = useLazyGetEthUsdPriceQuery()
  const fetchEthUsdPrice = useCallback(async () => {
    try {
      const data = await getEthUsdPrice({}).unwrap()
      dispatch(setEthUsdPrice(data?.ethereum?.usd ?? 0))
    } catch (err) {
      console.error(err)
    }
  }, [dispatch, getEthUsdPrice])

  useEffect(() => {
    // Verify if the wallet is connected

    const isWalletConnected = !!isConnected && !!address
    if (!isWalletConnected) return
    if (isLoggingOut) return
    verify({ address })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, address, isLoggingOut])

  const refreshDynamicUser = useRefreshUser()
  useEffect(() => {
    // Refresh dynamic connection if the user is logged in but dynamic is not connected

    if (!!user?.id && sdkHasLoaded && !isConnected) {
      refreshDynamicUser()
    }
  }, [address, isConnected, refreshDynamicUser, sdkHasLoaded, user?.id])

  useEffect(() => {
    // Logout if the user is not connected
    if (sdkHasLoaded && !isConnected && !address && !!account) {
      logout()
    }
  }, [account, isConnected, logout, address, user?.id, sdkHasLoaded])

  useEffect(() => {
    if (!primaryWallet) return
    const { address } = primaryWallet ?? {}
    updateBalance(address)
    if (!activeTokenSaleAddress) return
    fetchAndSetActiveTokenSaleBalance(address)
  }, [
    primaryWallet,
    updateBalance,
    activeTokenSaleAddress,
    fetchAndSetActiveTokenSaleBalance,
  ])

  useEffect(() => {
    fetchEthUsdPrice()

    const ethPriceInterval = setInterval(() => {
      fetchEthUsdPrice()
    }, FIVE_MINUTES_IN_MILLISECONDS)

    return () => clearTimeout(ethPriceInterval)
  }, [fetchEthUsdPrice])

  return (
    <Web3Context.Provider
      value={{
        bscNetwork,
        ethereumNetwork,
        polygonNetwork,
        blastProvider,
        baseNetwork,
        initWeb3,
        updateBalance,
        poolsData,
        logout,
        isAuthenticating,
        isMetaMaskMobile,
        getReadOnlyContract,
        getWriteOnlyContract,
        provider,
        signer,
        openOnramp,
        contracts: {
          master: masterContract,
          masterRpc: masterRpcContract,
          lpToken: lpTokenContract,
          staking: stakingContract,
          admin: adminContract,
          access: accessContract,
          stakingOld: stakingOldContract,
          stakingV1: stakingV1Contract,
          router: routerContract,
          busd: busdContract,
        },
      }}
    >
      {children}
    </Web3Context.Provider>
  )
}

const useWeb3 = () => useContext(Web3Context)

export { NETWORK_VERSION } from './constants'
export default useWeb3
